import { Icon, Text } from '@mntn/ui-components'
import type React from 'react'
import { MfeThemeWrapper } from '../shared/mfe-theme-wrapper.tsx'
import type { AudienceAlertProps } from './types.ts'
import { useAudienceComparison } from './use-audience-comparison'

/**
 * FlightDeckAudienceAlert - Compares original vs applied audience sizes

 * Displays a warning when the applied audience is smaller than the original
 * audience, indicating potential underdelivery on deal commitments.
 * 
 * Fails silently (returns null) for any missing data or errors to avoid
 * breaking the host application.
 */
// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const FlightDeckAudienceAlert: React.FC<AudienceAlertProps> = ({
  appliedAudienceId,
  campaignId: campaignGroupId, // Rename prop to match Select's naming convention
}) => {
  // Early return if props are missing
  if (!appliedAudienceId || !campaignGroupId) {
    return null
  }

  const {
    data: comparison,
    isLoading,
    isError,
  } = useAudienceComparison({
    appliedAudienceId,
    campaignGroupId,
  })

  if (isLoading || isError || !comparison?.shouldShowAlert) {
    return null
  }

  return (
    <MfeThemeWrapper>
      <div className={'p-4 border flex items-start gap-3 my-4 mx-6 bg-warning/10 border-warning'}>
        <Icon name="Info" className="size-5 text-warning mt-0.5" />
        <div className="flex-1">
          <Text variant="base-sb" className="text-grey-g4 mb-1">
            Audience Size Warning
          </Text>
          <Text variant="base-sm" className="text-grey-g4">
            Your current audience size is projected to underdeliver on your purchased deal commitment. Please expand
            your audience.
          </Text>
        </div>
      </div>
    </MfeThemeWrapper>
  )
}
